import React, { useEffect, useRef, useState } from "react"
import { navigate } from '@reach/router'
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit } from "components/table/inputs"
// Controls
import Controls from "components/asset-management/comparison/controls/"
// Data
import api_match from "data/interface/api"
import automationService from "data/automation"
import carouselService from "data/carousel"
import comparisonService, { Comparison } from "data/comparison"
import keyService, * as chord from "data/key"
import { handleReferences } from "data/lib/handle-references"
import validationService, { IValidationErrors } from "data/validate"
// Styles
import { Button_s } from 'styles/app-styles'
import { ComparisonTable_s } from "components/asset-management/comparison/comparison-styles"

export type ContextRow = {
  comparisonid: number
  code: string
}

type PropTypes = {
  tableId: string
  data: Comparison[]
}

const ComparisonTable = (props: PropTypes) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const nonSystemMenu = [
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: async e => {
      const comparison = await comparisonService.copy(contextRow.comparisonid, contextRow.code)
    }},
    {label: 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => {
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
  ]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [_errors, setErrors] = useState<any>()
  const [contextRow, setContextRow] = useState<ContextRow>({comparisonid: 0, code: ''})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('comparison')

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            comparisonService.create()
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[currentRecordIndex];
            if (!currentRow.systeminput) showDeleteConfirmation({comparisonid: currentRow.comparisonid, code: currentRow.code}, currentRecordIndex + 1)
          }
          break
      }
    })

    const validationSubscription = validationService.stream().subscribe((e) => {
      setErrors(e)
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      keySubscription.unsubscribe()
      validationSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    const comparison = field ? _.omit(_.cloneDeep(rowData), 'index') : value
    _.set(comparison, field, value);
    comparisonService.patch(comparison);
  }

  const refresh = async () => {
    const comparisons = await comparisonService.fetchComparisons()
  }

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete ${record.code}?  Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        comparisonService.deleteOne(record.comparisonid)
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code
    }))
  }

  function updateContextMenu(originalEvent){
    setMenu(nonSystemMenu)
    _.invoke(menuRef.current, "show", originalEvent)
  }

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <ComparisonTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth={"45px"}
          paginatorPosition="bottom"
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            filter
            header="Code"
            {...field('code')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px", maxWidth: "250px"}} className="p-editable-column comparison-name"
            filter
            header="Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
          />
          {/* <Column style={{minWidth: "250px", maxWidth: "250px"}} className="p-editable-column"
            filter
            header="Type"
            {...field('type')}
            editor={props => TextEdit({persist, ...props})}
          /> */}
          <Column style={{minWidth: "100px"}} className="p-editable-column"
            header="Details"
            body={(rowData, props) => <Button_s className="detailed-route-button" style={{height: '26px'}} onClick={()=>navigate(`/n/asset-management/comparison/${rowData.comparisonid}`)}>View/Edit</Button_s>}
          />
        </DataTable>
        { carouselData && carouselData['show'] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={500} /> }
        { deleteConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </ComparisonTable_s>
    </div>
  )
}

export default ComparisonTable
